import React from 'react';
import { EmissionsModel } from '../../../typings/general';

import { FindADealerCtaSection } from '../../find-a-dealer-cta-section';
import { ParagraphSection } from '../../paragraph-section';
import { TableInformation } from '../table-information';
import { MatchInformationContainer } from './styled';
import { getEnginesLinkInformation } from '../../../utils';
import { useFeatureAppConfig } from '../../../services/use-feature-app-config';

interface MatchInformationProps {
  emissionsData: EmissionsModel;
}

export const MatchInformation: React.FC<MatchInformationProps> = props => {
  const { faHostServerUrl = '' } = useFeatureAppConfig();
  const { emissionsData } = props;

  let eligibilityText =
    'An emissions modification has not been approved for this vehicle by EPA and CARB.';
  let emissionText = `Please find details on the modification for the vehicle in the <b>[Emissions Modification Disclosure.](${
    getEnginesLinkInformation(emissionsData, faHostServerUrl).url
  })</b>`;
  let extendedText = '';
  let addition = '';

  if (emissionsData.remedyAvailable) {
    eligibilityText =
      'An emissions modification has been approved for this vehicle by EPA and CARB. Please see below for additional information regarding the emissions modification.';

    if (!emissionsData.repairComplete) {
      eligibilityText =
        'An emissions modification has been approved by EPA and CARB and is available for this vehicle.';
      if (emissionsData.extendedWarrantyApplied) {
        eligibilityText = `An emissions modification has been approved for this vehicle by EPA and CARB. Our records show that a partial modification has been completed, and additional modification(s) may still be required.`;
      }
    } else {
      eligibilityText =
        'An emissions modification has been approved for this vehicle by EPA and CARB. Our records show that a modification has been completed.';
      if (
        emissionsData.extendedWarrantyApplied &&
        emissionsData.generation === '1'
      ) {
        eligibilityText = `An emissions modification has been approved for this vehicle by EPA and CARB. Please see below for additional information regarding the emissions modification.`;
      }
      if (
        emissionsData.extendedWarrantyApplied &&
        ((emissionsData.generation === '2' &&
          emissionsData.subGeneration === '2' &&
          emissionsData.engineSize === '3.0L') ||
          emissionsData.generation === '3' ||
          emissionsData.gen3Phase1RepairComplete)
      ) {
        eligibilityText = `An emissions modification has been approved for this vehicle by EPA and CARB. Our records show that the modification is complete and this vehicle has an <a href="http://vwcourtsettlement.com">Extended Emissions Warranty</a>.`;
      }
    }
  }

  if (emissionsData.extendedWarranty.length > 0) {
    let fmiles = '';
    let fyear = '';
    let fmonths = '';

    let smiles = '';
    let syear = '';
    let smonths = '';

    emissionsData.extendedWarranty.forEach(function(warranty, index) {
      try {
        let name = warranty.name.split('/');
        let cmiles = name[1]?.replace('MI', '');
        let cyear = name[0]
          .replace('TDI Warranty Extension-', '')
          .split('yr')[0];
        let cmonths = '';

        if (name[0].includes('mo')) {
          cmonths =
            'and ' +
            name[0]
              .replace('TDI Warranty Extension-', '')
              .split('yr')[1]
              .split('mo')[0] +
            ' months';
        }

        if (index === 0) {
          fmiles = cmiles;
          fyear = cyear;
          fmonths = cmonths;
        } else {
          smiles = cmiles;
          syear = cyear;
          smonths = cmonths;
        }
      } catch (e) {}
    });

    extendedText = `The vehicle has an Extended Emissions Warranty that runs for ${syear} years ${smonths} or ${smiles} miles from the original in-service date. Please see your VW dealership for additional information including the vehicle's in-service date.`;
    if (
      emissionsData.extendedWarrantyApplied &&
      emissionsData.extendedWarranty.length > 1
    ) {
      addition = `In addition, and concurrently, the Extended Emissions Warranty runs for ${fyear} years ${fmonths} or ${fmiles} miles (whichever occurs first) from the date of modification or first dealer resale post-modification. Your vehicle was modified on <strong> ${
        emissionsData.repairDate
      }</strong> and <strong> ${emissionsData.repairMileage.toLocaleString()}</strong> miles. Please see your VW dealership for additional information on the vehicle's post-modification dealer re-sale date.`;
    }
  }

  return (
    <MatchInformationContainer>
      <ParagraphSection
        head="Volkswagen Emissions Modification Programs"
        marginBottom="0"
      />
      <TableInformation emissionsData={emissionsData} />

      {emissionsData && !emissionsData.remedyAvailable && (
        <>
          <ParagraphSection
            head="Vehicle Eligibility and Emissions Modification Status"
            richtext={eligibilityText}
          />
        </>
      )}
      {emissionsData && emissionsData.remedyAvailable && (
        <>
          <ParagraphSection
            head="Vehicle Eligibility and Emissions Modification Status"
            richtext={eligibilityText}
          />

          <ParagraphSection
            head="Emissions Modification Disclosure"
            richtext={emissionText}
          />

          {emissionsData.extendedWarranty.length > 0 && (
            <ParagraphSection
              head="Extended Emissions Warranty Information*"
              richtext={extendedText}
            />
          )}
          {emissionsData.extendedWarranty.length > 0 &&
            emissionsData.extendedWarrantyApplied && (
              <ParagraphSection richtext={addition} />
            )}

          {emissionsData.extendedWarranty.length > 0 && (
            <ParagraphSection
              textColor="#515a5e"
              richtext={
                '*For vehicles that have completed repairs in Canada, please see the Canadian extended warranty terms.'
              }
            />
          )}

          {emissionsData.extendedWarranty.length === 0 && (
            <ParagraphSection
              head="Extended Emissions Warranty Information"
              richtext={
                'This vehicle DOES NOT have an Extended Emissions Warranty as our records indicate it still requires an approved emissions modification.'
              }
            />
          )}

          <ParagraphSection
            head="Schedule a Service Appointment"
            richtext={
              'Stay informed about additional required emissions modifications. Once a modification is available, please contact an authorized VW dealer to schedule an appointment.'
            }
          />
        </>
      )}

      <FindADealerCtaSection
        showFind={emissionsData ? emissionsData.remedyAvailable : false}
      />
    </MatchInformationContainer>
  );
};
